import parse from 'html-react-parser';
import React, { CSSProperties, useEffect, useState } from 'react';
import { TCoreImageOptions } from '../types/options';
import styles from './image.module.css';
import { fetchAndCacheSvg } from './fetchAndCacheSvg';

export type TColorSvgImageProps = {
    src: string;
    spinnerSize: number | string;
    dataTestId?: string;
    spinnerDelay?: number;
    defaultSrc?: string;
} & Pick<TCoreImageOptions, 'width' | 'height' | 'borderRadius' | 'iconColor' | 'iconColorDisabled' | 'isDisabled'>;

type TSvgFetchResult = { src: string; data: string | null; error: Error | null } | null;

const DEFAULT_SPINNER_DELAY_MS = 300;

const ColorSvgImage = ({
    src,
    iconColor,
    iconColorDisabled,
    isDisabled,
    width,
    height,
    borderRadius,
    dataTestId,
    spinnerSize,
    spinnerDelay = DEFAULT_SPINNER_DELAY_MS,
    defaultSrc,
}: TColorSvgImageProps) => {
    const [result, setResult] = useState<TSvgFetchResult>(null);

    // Derive current values from fetch result — no sync setState in effects
    const svgData = result?.src === src ? result.data : null;
    const loading = result === null || result.src !== src;
    const error = result?.src === src ? result.error : null;

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            let fetched = await fetchAndCacheSvg(src);
            if (fetched.text === null && defaultSrc) {
                fetched = await fetchAndCacheSvg(defaultSrc);
            }
            if (cancelled) return;
            setResult({ src, data: fetched.text, error: null });

            if (fetched.revalidation) {
                try {
                    const fresh = await fetched.revalidation;
                    if (!cancelled && fresh !== null) {
                        setResult({ src, data: fresh, error: null });
                    }
                } catch (err) {
                    console.error('Error during SVG revalidation:', err);
                }
            }
        };

        run().catch((err: unknown) => {
            if (!cancelled) {
                setResult({ src, data: null, error: err as Error });
            }
        });

        return () => {
            cancelled = true;
        };
    }, [src, defaultSrc]);

    if (loading) {
        return (
            <div style={{ width, height, borderRadius, position: 'relative', flexShrink: 0 }}>
                <div
                    className={styles.spinnerContainer}
                    style={
                        {
                            '--spinner-delay': `${spinnerDelay}ms`,
                        } as CSSProperties
                    }
                >
                    <div className={styles.spinner} style={{ width: spinnerSize, height: spinnerSize }} />
                </div>
            </div>
        );
    }

    if (!svgData || error) {
        return <div data-test={dataTestId} style={{ width, height, borderRadius } as CSSProperties} />;
    }

    const colorForIcon = isDisabled ? iconColorDisabled : iconColor;

    return (
        <div data-test={dataTestId} style={{ width, height, borderRadius, fill: colorForIcon, color: colorForIcon } as CSSProperties}>
            {parse(svgData)}
        </div>
    );
};

export { ColorSvgImage };
